import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Shield, KeyRound, CheckCircle2, Eye, EyeOff } from "lucide-react";

export default function SuperAdminForgotPassword() {
    const navigate = useNavigate();
    const [step, setStep] = useState("email"); // email | otp | done
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [newPass, setNewPass] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submitEmail = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try {
            await api.post("/auth/super-admin/forgot-password", { email: email.trim() });
            setStep("otp");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Request failed");
        } finally {
            setLoading(false);
        }
    };

    const submitReset = async (e) => {
        e.preventDefault();
        setError("");
        if (newPass.length < 8) { setError("Password must be at least 8 characters"); return; }
        if (!/^\d{6}$/.test(otp.trim())) { setError("Code must be 6 digits"); return; }
        setLoading(true);
        try {
            await api.post("/auth/super-admin/reset-password", {
                email: email.trim(),
                otp: otp.trim(),
                new_password: newPass,
            });
            setStep("done");
            setTimeout(() => navigate("/super-admin/login"), 2500);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Reset failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4 py-12"
            style={{ background: "linear-gradient(135deg, #0F1A2E 0%, #1F1F1F 100%)" }}
            data-testid="super-admin-forgot-page"
        >
            <div className="w-full max-w-md bg-white border border-ink/10 rounded-lg shadow-lift overflow-hidden">
                <div className="px-8 py-7 border-b border-ink/5" style={{ background: "linear-gradient(135deg, #0F1A2E 0%, #1F1F1F 100%)" }}>
                    <div className="flex items-center gap-3 text-white">
                        <div className="w-10 h-10 rounded-md bg-white/10 backdrop-blur flex items-center justify-center">
                            <Shield className="w-5 h-5 text-white" strokeWidth={2} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold">Account recovery</div>
                            <div className="font-display font-bold text-lg">Super-admin reset</div>
                        </div>
                    </div>
                </div>

                <div className="p-8">
                    {step === "email" && (
                        <form onSubmit={submitEmail} data-testid="super-admin-forgot-email-form">
                            <p className="text-sm text-ink/60 mb-6 leading-relaxed">
                                Enter the super-admin email and we'll send a 6-digit code to verify it's you.
                                The code is valid for 15 minutes.
                            </p>
                            <label className="overline">Super-admin email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoFocus
                                placeholder="support@yourdomain.com"
                                data-testid="super-admin-forgot-email-input"
                                className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 px-0 focus:border-klein focus:outline-none text-ink"
                            />
                            {error && (
                                <div className="mt-5 border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive rounded-md" data-testid="super-admin-forgot-error">
                                    {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="super-admin-forgot-submit"
                                className="mt-7 w-full bg-ink text-white px-6 py-3.5 font-medium hover:bg-ink/90 disabled:opacity-60 rounded-md"
                            >
                                {loading ? "Sending…" : "Send reset code"}
                            </button>
                        </form>
                    )}

                    {step === "otp" && (
                        <form onSubmit={submitReset} data-testid="super-admin-reset-form">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-klein/10 text-klein mb-4 rounded-md">
                                <KeyRound className="w-6 h-6" />
                            </div>
                            <p className="text-sm text-ink/60 mb-6 leading-relaxed">
                                A 6-digit code has been emailed to <span className="font-semibold text-ink">{email}</span>.
                                Enter it below along with your new password.
                            </p>
                            <label className="overline">6-digit code</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="\d{6}"
                                maxLength={6}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                required
                                autoFocus
                                placeholder="••••••"
                                data-testid="super-admin-otp-input"
                                className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 px-0 focus:border-klein focus:outline-none text-ink font-mono text-2xl tracking-[0.5em] text-center"
                            />
                            <label className="overline mt-6 block">New password</label>
                            <div className="relative">
                                <input
                                    type={showPass ? "text" : "password"}
                                    value={newPass}
                                    onChange={(e) => setNewPass(e.target.value)}
                                    minLength={8}
                                    required
                                    placeholder="Min 8 characters"
                                    data-testid="super-admin-new-password-input"
                                    className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 pr-10 px-0 focus:border-klein focus:outline-none text-ink"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPass((s) => !s)}
                                    tabIndex={-1}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-ink/40 hover:text-ink"
                                >
                                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            {error && (
                                <div className="mt-5 border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive rounded-md" data-testid="super-admin-reset-error">
                                    {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="super-admin-reset-submit"
                                className="mt-7 w-full bg-ink text-white px-6 py-3.5 font-medium hover:bg-ink/90 disabled:opacity-60 rounded-md"
                            >
                                {loading ? "Resetting…" : "Set new password"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setStep("email"); setOtp(""); setNewPass(""); setError(""); }}
                                className="mt-4 w-full text-xs uppercase tracking-[0.15em] text-ink/50 hover:text-klein"
                                data-testid="super-admin-back-step"
                            >
                                ← Use a different email
                            </button>
                        </form>
                    )}

                    {step === "done" && (
                        <div className="text-center py-4" data-testid="super-admin-reset-done">
                            <div className="inline-flex items-center justify-center w-14 h-14 bg-green-50 text-green-700 mb-4 rounded-full">
                                <CheckCircle2 className="w-7 h-7" />
                            </div>
                            <h2 className="font-display font-bold text-xl text-ink">Password updated</h2>
                            <p className="mt-3 text-sm text-ink/60">
                                Redirecting you to sign in…
                            </p>
                        </div>
                    )}

                    <div className="mt-7 pt-6 border-t border-ink/10 text-center">
                        <Link to="/super-admin/login" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink/50 hover:text-klein" data-testid="super-admin-back-to-login">
                            <ArrowLeft className="w-3 h-3" /> Back to super-admin sign in
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
